/**
 * Payer refund: (1) reason + details → (2) OTP to client_phone → (3) success.
 * Authenticated by payment link token only (does not use broker session).
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { HugeiconsIcon } from '@hugeicons/react';
import { Cancel01Icon } from '@hugeicons/core-free-icons';
import { Button } from '@components/Button/Button';
import { useToast } from '@contexts/ToastContext';
import { Select, type SelectOption } from '@components/Select/Select';
import { SaudiRiyalSymbol } from '@components/SaudiRiyalSymbol';
import { payerRefundStart, payerRefundConfirm, type ApiError } from '@services/api';
import { requestRefundBodySchema } from '@schemas/formSchemas';

const REFUND_REASONS = ['Incomplete Deal', 'Change of Mind', 'Property Issue', 'Other'] as const;

function formatSar(amount: number): string {
  return new Intl.NumberFormat('en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
}

export interface PayerRefundFlowModalProps {
  open: boolean;
  onClose: () => void;
  dealId: string;
  paymentToken: string;
  totalCommission: number;
  onCompleted: () => void;
}

type Step = 'form' | 'otp';

export function PayerRefundFlowModal({
  open,
  onClose,
  dealId,
  paymentToken,
  totalCommission,
  onCompleted,
}: PayerRefundFlowModalProps) {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [step, setStep] = useState<Step>('form');
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [otpDestination, setOtpDestination] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);

  const reasonOptions: SelectOption[] = [
    {
      value: '',
      label: t('payer.refundFlow.selectReason', { defaultValue: 'Select reason' }),
    },
    ...REFUND_REASONS.map((r) => ({
      value: r,
      label: t(`payer.refundFlow.reasons.${r.replace(/\s+/g, '')}`, { defaultValue: r }),
    })),
  ];

  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset refund flow state when modal opens
    setStep('form');
    setReason('');
    setDetails('');
    setOtp('');
    setOtpDestination('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleStart = useCallback(async () => {
    const parsed = requestRefundBodySchema.safeParse({
      reason: reason.trim(),
      details: details.trim() || undefined,
    });
    if (!parsed.success) {
      const msg =
        parsed.error.flatten().formErrors[0] ??
        t('payer.refundFlow.reasonRequired', { defaultValue: 'Please select a refund reason.' });
      addToast({ variant: 'warning', title: msg });
      return;
    }
    setLoading(true);
    try {
      const { data } = await payerRefundStart(dealId, paymentToken, parsed.data);
      setOtpDestination(data.otpDestination);
      setStep('otp');
      addToast({
        variant: 'info',
        title: t('payer.refundFlow.nextStepTitle', { defaultValue: 'Check your mobile' }),
        description: t('payer.refundFlow.leadBrokerNote', {
          defaultValue:
            'Lead broker will be notified for approval after you confirm with the code sent to your mobile.',
        }),
      });
    } catch (e) {
      const msg = (e as ApiError).message ?? t('common.error', { defaultValue: 'Something went wrong.' });
      addToast({ variant: 'error', title: msg });
    } finally {
      setLoading(false);
    }
  }, [dealId, paymentToken, reason, details, t, addToast]);

  const handleConfirmOtp = useCallback(async () => {
    const digits = otp.replace(/\D/g, '').slice(0, 6);
    if (digits.length !== 6) return;
    setLoading(true);
    try {
      await payerRefundConfirm(dealId, paymentToken, digits);
      addToast({
        variant: 'success',
        title: t('payer.refundFlow.successTitle', { defaultValue: 'Your request has been submitted successfully' }),
        description: t('payer.refundFlow.successSubtitle', {
          defaultValue: 'Your refund request will be reviewed. The lead broker has been notified.',
        }),
      });
      onCompleted();
      onClose();
    } catch (e) {
      const msg = (e as ApiError).message ?? t('auth.otp.invalidCode', { defaultValue: 'Invalid code.' });
      addToast({ variant: 'error', title: msg });
    } finally {
      setLoading(false);
    }
  }, [dealId, paymentToken, otp, onCompleted, onClose, t, addToast]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 font-sans"
      role="dialog"
      aria-modal="true"
      aria-labelledby="payer-refund-dialog-title"
    >
      <div className="absolute inset-0 bg-black/50" aria-hidden onClick={onClose} />
      <div className="relative w-full max-w-md rounded-card border border-surface-border bg-surface-cards shadow-xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between gap-4 mb-6">
          <h2 id="payer-refund-dialog-title" className="text-lg font-bold text-primary sm:text-xl">
            {step === 'form' && t('payer.refundFlow.titleRequest', { defaultValue: 'Refund Request' })}
            {step === 'otp' && t('payer.refundFlow.confirmTitle', { defaultValue: 'Confirm Transaction' })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px] text-primary/70 hover:bg-surface-gray-lighter hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label={t('common.close', { defaultValue: 'Close' })}
          >
            <HugeiconsIcon icon={Cancel01Icon} size={20} />
          </button>
        </div>

        {step === 'form' && (
          <>
            <p className="text-sm font-medium text-primary/80 mb-1">
              {t('payer.refundFlow.totalCommission', { defaultValue: 'Total Commission Amount' })}
            </p>
            <p className="text-2xl font-semibold text-secondary tabular-nums mb-6 flex items-center gap-1">
              {formatSar(totalCommission)}
              <SaudiRiyalSymbol className="inline-block h-7 w-7 text-secondary" aria-hidden />
            </p>
            <div className="space-y-4">
              <div>
                <label htmlFor="payer-refund-reason" className="mb-1 block text-sm font-medium text-primary/80">
                  {t('payer.refundFlow.refundReason', { defaultValue: 'Refund Reason' })}
                </label>
                <Select
                  id="payer-refund-reason"
                  value={reason}
                  onChange={setReason}
                  options={reasonOptions}
                  fullWidth
                  triggerClassName="min-h-[50px]"
                />
              </div>
              <div>
                <label htmlFor="payer-refund-details" className="mb-1 block text-sm font-medium text-primary/80">
                  {t('payer.refundFlow.additionalDetails', { defaultValue: 'Additional Details (Optional)' })}
                </label>
                <textarea
                  id="payer-refund-details"
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  rows={3}
                  className="w-full rounded-[16px] border border-surface-gray-darker bg-surface-cards px-4 py-3 font-sans text-base text-primary placeholder:text-primary/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-y min-h-[88px]"
                  maxLength={2000}
                />
              </div>
            </div>
            <div className="mt-6">
              <Button
                type="button"
                variant="primary"
                size="lg"
                fullWidth
                className="rounded-[16px]"
                onClick={() => void handleStart()}
                loading={loading}
                disabled={loading}
              >
                {t('payer.refundFlow.submit', { defaultValue: 'Submit Refund Request' })}
              </Button>
            </div>
          </>
        )}

        {step === 'otp' && (
          <>
            <p className="text-sm text-primary/80 mb-1">
              {t('payer.refundFlow.enterCodeSentTo', { defaultValue: 'Enter the code sent to your mobile Number:' })}
            </p>
            <p className="text-base font-medium text-primary mb-4 dir-ltr break-all" dir="ltr">
              {otpDestination}
            </p>
            <label htmlFor="payer-refund-otp" className="mb-1 block text-sm font-medium text-primary/80">
              {t('payer.refundFlow.otpLabel', { defaultValue: 'OTP' })}
            </label>
            <input
              id="payer-refund-otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full rounded-[16px] border border-surface-gray-darker bg-surface-cards px-4 py-3 font-sans text-base text-primary tabular-nums focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              dir="ltr"
            />
            <div className="mt-6">
              <Button
                type="button"
                variant="primary"
                size="lg"
                fullWidth
                className="rounded-[16px]"
                onClick={() => void handleConfirmOtp()}
                loading={loading}
                disabled={loading || otp.length !== 6}
              >
                {t('payer.refundFlow.confirm', { defaultValue: 'Confirm' })}
              </Button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
